import { validateSAPhone } from "./stage2/submission";
import type {
  A1Draft,
  A1ItemResponse,
  A1Profile,
  A1Summary,
  Indicator,
} from "./types";

export function emptyResponse(): A1ItemResponse {
  return { weight: null, availabilityView: "", comment: "" };
}

export function emptyProfile(): A1Profile {
  return {
    consentToParticipate: false,
    completionMode: "self",
    respondentName: "",
    contact: "",
    organisationName: "",
    organisationType: "",
    professionalRole: "",
    expertiseDimensions: [],
    geographicExperienceType: "national",
    geographicExperienceDetail: "",
    yearsExperienceBand: "",
    generalComment: "",
    municipalityCode: "",
    sourceRole: "",
    sourceOrganisation: "",
  };
}

export function createDraft(
  catalogueVersion: string,
  formVersion: string,
  scopedDimensions: string[] = [],
): A1Draft {
  return {
    submissionId: crypto.randomUUID(),
    catalogueVersion,
    formVersion,
    updatedAt: new Date().toISOString(),
    currentIndex: 0,
    scopedDimensions,
    profile: emptyProfile(),
    responses: {},
    receipt: null,
  };
}

export function isValidWeight(value: unknown): value is number | null {
  return (
    value === null ||
    (typeof value === "number" &&
      Number.isInteger(value) &&
      value >= 1 &&
      value <= 10)
  );
}

export function profileErrors(profile: A1Profile): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!profile.consentToParticipate) {
    errors.consentToParticipate = "Confirm voluntary participation to continue.";
  }
  if (!profile.organisationName.trim()) {
    errors.organisationName = "Enter the organisation or select Independent / Other.";
  }
  if (!profile.organisationType) {
    errors.organisationType = "Choose an organisation type.";
  }
  if (!profile.professionalRole.trim()) {
    errors.professionalRole = "Enter a role or area of responsibility.";
  }
  if (!profile.municipalityCode.trim()) {
    errors.municipalityCode =
      "Type the municipality or area this weighting applies to (or National).";
  }
  if (profile.respondentName.trim().length < 3) {
    errors.respondentName = "Enter the respondent's name.";
  }
  if (!validateSAPhone(profile.contact).ok) {
    errors.contact = "Please enter valid SA number.";
  }
  if (!profile.geographicExperienceDetail.trim()) {
    errors.geographicExperienceDetail = "Describe the main geographic area.";
  }
  if (profile.completionMode === "student-assisted") {
    if (!profile.sourceRole.trim()) {
      errors.sourceRole = "Record the respondent’s role for assisted capture.";
    }
    if (!profile.sourceOrganisation.trim()) {
      errors.sourceOrganisation =
        "Record the respondent’s organisation for assisted capture.";
    }
  }
  return errors;
}

export function summariseDraft(
  draft: A1Draft,
  indicators: Indicator[],
): A1Summary {
  const responses = indicators.map(
    (indicator) => draft.responses[indicator.indicatorId] ?? emptyResponse(),
  );
  const weighted = responses.filter((response) => response.weight !== null);
  const scopedIndicators = draft.scopedDimensions.length
    ? indicators.filter((indicator) =>
        draft.scopedDimensions.includes(indicator.indicatorId.slice(0, 2)),
      )
    : indicators;
  const scopedWeightedCount = scopedIndicators.filter(
    (indicator) => draft.responses[indicator.indicatorId]?.weight != null,
  ).length;
  return {
    weightedCount: weighted.length,
    blankCount: indicators.length - weighted.length,
    averageWeight: weighted.length
      ? weighted.reduce((total, response) => total + (response.weight ?? 0), 0) /
        weighted.length
      : null,
    commentedCount: responses.filter((response) => response.comment.trim()).length,
    differentCount: indicators.filter((indicator) => {
      const weight = draft.responses[indicator.indicatorId]?.weight;
      return weight != null && weight !== indicator.proposedWeight;
    }).length,
    scopedWeightedCount,
    scopedItemCount: scopedIndicators.length,
    scopedCompletion: scopedIndicators.length
      ? scopedWeightedCount / scopedIndicators.length
      : 0,
  };
}
